import * as React from 'react';
import { useIntl } from 'react-intl';

import { Text } from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';

import { useDeliveryDrawer } from './DeliveryDrawer';
import { DeliveryLine } from './DeliveryLine';
import { messages as deliveryMessages } from './messages';
import {
  Container,
  DeliveryList,
  FlexRow,
  Select,
  SelectWrapper,
  TopDeliveryInfo,
} from './styled';
import {
  getDeliveryDescription,
  getIconByDelivery,
  groupPossibleDeliveries,
  parseThresholdTime,
  shouldShowFreeShipping,
} from './utils';

export enum DeliveriesDisplayMode {
  Static = 'Static',
  Expandable = 'Expandable',
}

export interface IDeliveryTimeProps {
  deliveries: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >['deliveries'];
  displayMode?: DeliveriesDisplayMode;
  selectedCatalogId: string;
}

export const DeliveryTime: React.FC<IDeliveryTimeProps> = React.memo(
  ({ deliveries, selectedCatalogId }) => {
    const { formatMessage, formatTime } = useIntl();

    const groupedDeliveries = React.useMemo(
      () => groupPossibleDeliveries(deliveries, true),
      [deliveries]
    );

    const { openDeliveryDrawer } = useDeliveryDrawer(
      groupedDeliveries,
      selectedCatalogId
    );

    const handleTopDeliveryKeyDown = (
      e: React.KeyboardEvent<HTMLElement>,
      delivery: NonNullable<
        GetCatalogProductViewQuery['productDetailByCatalogMasterId']
      >['deliveries'][number]['delivery']
    ) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDeliveryDrawer(delivery);
      }
    };

    return (
      <ErrorBoundary>
        <Container data-testid="pd-delivery-time" id="pdDeliveryTime">
          <SelectWrapper>
            <Select style={{ cursor: 'default' }}>
              <DeliveryList role="list" tabIndex={-1}>
                <FlexRow
                  key={0}
                  role="listitem"
                  data-testid="pd-delivery-list-item"
                >
                  {groupedDeliveries.map(
                    // eslint-disable-next-line no-shadow,@typescript-eslint/no-shadow
                    ({ deliveries, getTopDeliveryName }, index) => {
                      const thresholdMessage = (
                        <Text
                          typography="labelRegular400"
                          color="text.secondary"
                          style={{ display: 'inline' }}
                        >
                          {deliveries[0]?.thresholdTime
                            ? `, ${formatMessage(
                                deliveryMessages.deliveryIfYouOrderBefore,
                                {
                                  time: formatTime(
                                    parseThresholdTime(
                                      deliveries[0].thresholdTime
                                    )
                                  ),
                                }
                              )}`
                            : ''}
                        </Text>
                      );

                      return (
                        deliveries.length > 0 && (
                          <TopDeliveryInfo
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            onClick={() =>
                              openDeliveryDrawer(deliveries[0].delivery)
                            }
                            onKeyDown={(e) =>
                              handleTopDeliveryKeyDown(
                                e,
                                deliveries[0].delivery
                              )
                            }
                            tabIndex={0}
                            role="button"
                          >
                            <DeliveryLine
                              showIcon={true}
                              icon={getIconByDelivery(deliveries[0], true)}
                              isInactive={
                                deliveries[0].disallowedReason !== null
                              }
                              name={getTopDeliveryName(
                                deliveries[0],
                                formatMessage
                              )}
                              description={
                                <>
                                  {getDeliveryDescription(deliveries[0])}
                                  {thresholdMessage}
                                </>
                              }
                              price={deliveries[0].price ?? 0}
                              freeShippingText={
                                shouldShowFreeShipping(
                                  deliveries[0],
                                  selectedCatalogId
                                )
                                  ? formatMessage(
                                      deliveryMessages.deliveryFreeShipping
                                    )
                                  : undefined
                              }
                            />
                          </TopDeliveryInfo>
                        )
                      );
                    }
                  )}
                </FlexRow>
              </DeliveryList>
            </Select>
          </SelectWrapper>
        </Container>
      </ErrorBoundary>
    );
  }
);
